import { useEffect, useState } from 'react';
import { Send, Clock, CheckCircle2, AlertTriangle, Pencil } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';

/**
 * ENVÍOS DEL SUPLEMENTO AL CARRIER — el tramo interno, capturado en vivo.
 *
 * El ciclo del suplemento son ~20 días nuestros (estimado del carrier → nuestro envío) y ~11 del
 * carrier. El tramo interno es casi el doble y es la única mitad que el equipo controla, pero
 * hasta ahora no se registraba en ningún lado: se infería comparando fechas impresas en PDFs.
 * Este panel es el que lo captura. Ver migración 0375.
 *
 * Dos cosas de diseño que no son cosméticas:
 *  · Los días de espera se muestran con semáforo desde el envío, porque un suplemento mandado y
 *    sin respuesta es exactamente lo que sostiene los 134 claims parados en Supplementing.
 *  · La razón de cambio aparece SOLA a partir de la segunda edición, que es cuando el backend la
 *    exige. Pedirla siempre molesta; no pedirla nunca fue lo que dejó 61 fechas de cheque
 *    editadas sin explicación.
 */

type Envio = {
  id: string; round: number; sent_on: string; sent_at: string;
  amount_cents: number | null; method: string; carrier_reference: string | null;
  adjuster_name: string | null; notes: string | null;
  resolved_at: string | null; outcome: string | null; resolved_amount_cents: number | null;
  edit_count: number; sent_by: string | null; resolved_by: string | null;
  dias_esperando: number | null;
};

const money = (c: number | null) => (c == null ? '—' : `$${Math.round(c / 100).toLocaleString('en-US')}`);
const METODOS = ['email', 'portal', 'mail', 'fax', 'phone', 'other'] as const;
const DESENLACES = ['approved', 'partial', 'denied', 'withdrawn'] as const;

/** Semáforo de espera: a los 30 días amarillo, a los 60 rojo. */
function colorEspera(d: number | null): string {
  if (d == null) return 'var(--muted)';
  if (d >= 60) return 'var(--red)';
  if (d >= 30) return 'var(--orange)';
  return 'var(--green)';
}

export function SupplementSubmissions({ claimId }: { claimId: string }) {
  const { t } = useI18n();
  const [envios, setEnvios] = useState<Envio[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [editando, setEditando] = useState<Envio | null>(null);
  const hoy = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ sentOn: hoy, amount: '', method: 'email', adjusterName: '', carrierReference: '', notes: '', reason: '' });

  const cargar = () => {
    api.get<{ envios: Envio[] }>(`/api/claims/${claimId}/supplement-submissions`)
      .then((r) => setEnvios(r.envios))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  };
  useEffect(cargar, [claimId]);

  const cuerpo = () => ({
    sentOn: form.sentOn,
    amountCents: form.amount.trim() ? Math.round(Number(form.amount) * 100) : null,
    method: form.method,
    adjusterName: form.adjusterName.trim() || null,
    carrierReference: form.carrierReference.trim() || null,
    notes: form.notes.trim() || null,
  });

  const guardar = async () => {
    setGuardando(true); setErr(null);
    try {
      if (editando) {
        await api.patch(`/api/supplement-submissions/${editando.id}`, { ...cuerpo(), reason: form.reason.trim() || null });
      } else {
        await api.post(`/api/claims/${claimId}/supplement-submissions`, cuerpo());
      }
      setAbierto(false); setEditando(null);
      setForm({ sentOn: hoy, amount: '', method: 'email', adjusterName: '', carrierReference: '', notes: '', reason: '' });
      cargar();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally { setGuardando(false); }
  };

  const resolver = async (env: Envio, outcome: string) => {
    const monto = window.prompt(t('ss.resolvedAmountPrompt'), env.amount_cents != null ? String(Math.round(env.amount_cents / 100)) : '');
    if (monto === null) return;
    try {
      await api.post(`/api/supplement-submissions/${env.id}/resolve`, {
        outcome, resolvedAmountCents: monto.trim() ? Math.round(Number(monto) * 100) : null,
      });
      cargar();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };

  const abrirEdicion = (env: Envio) => {
    setEditando(env);
    setForm({
      sentOn: env.sent_on,
      amount: env.amount_cents != null ? String(Math.round(env.amount_cents / 100)) : '',
      method: env.method, adjusterName: env.adjuster_name ?? '',
      carrierReference: env.carrier_reference ?? '', notes: env.notes ?? '', reason: '',
    });
    setAbierto(true);
  };

  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>
          <Send size={15} strokeWidth={1.75} /> {t('ss.title')}
        </div>
        <button className="btn sm" onClick={() => { setEditando(null); setAbierto((o) => !o); }}>
          {abierto ? t('ss.cancel') : t('ss.markSent')}
        </button>
      </div>
      <p className="muted" style={{ margin: '0 0 10px', fontSize: 'var(--fs-2xs)' }}>{t('ss.help')}</p>

      {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)', marginBottom: 8 }}><AlertTriangle size={12} /> {err}</div>}

      {abierto && (
        <div style={{ border: '1px solid var(--line)', borderRadius: 10, padding: 10, marginBottom: 10, display: 'grid', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 8 }}>
            <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.sentOn')}
              <input type="date" max={hoy} value={form.sentOn} onChange={(e) => setForm({ ...form, sentOn: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.amount')}
              <input type="number" min="0" placeholder="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.method')}
              <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} style={{ width: '100%' }}>
                {METODOS.map((m) => <option key={m} value={m}>{t(`ss.method.${m}`)}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.adjuster')}
              <input value={form.adjusterName} onChange={(e) => setForm({ ...form, adjusterName: e.target.value })} style={{ width: '100%' }} />
            </label>
            <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.reference')}
              <input value={form.carrierReference} onChange={(e) => setForm({ ...form, carrierReference: e.target.value })} style={{ width: '100%' }} />
            </label>
          </div>
          {/* La razón solo cuando el backend la va a exigir (2ª edición en adelante). */}
          {editando && editando.edit_count >= 1 && (
            <label style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.reason')} <span style={{ color: 'var(--red)' }}>*</span>
              <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={{ width: '100%' }} placeholder={t('ss.reasonPh')} />
            </label>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn sm" disabled={guardando || !form.sentOn} onClick={guardar}>
              {guardando ? '…' : editando ? t('ss.saveEdit') : t('ss.save')}
            </button>
          </div>
        </div>
      )}

      {envios == null ? <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>…</span>
        : envios.length === 0 ? <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.none')}</span>
        : (
        <div style={{ display: 'grid', gap: 6 }}>
          {envios.map((e) => (
            <div key={e.id} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span className="badge gray" style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)' }}>#{e.round}</span>
              <span style={{ fontSize: 'var(--fs-2xs)' }}>{e.sent_on}</span>
              <span style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>{money(e.amount_cents)}</span>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t(`ss.method.${e.method}`)}{e.adjuster_name ? ` · ${e.adjuster_name}` : ''}</span>
              {e.resolved_at
                ? <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)' }}>
                    <CheckCircle2 size={11} /> {t(`ss.outcome.${e.outcome}`)} {e.resolved_amount_cents != null ? money(e.resolved_amount_cents) : ''}
                  </span>
                : <span style={{ fontSize: 'var(--fs-2xs)', color: colorEspera(e.dias_esperando), fontWeight: 700 }}>
                    <Clock size={11} /> {t('ss.waiting', { d: String(e.dias_esperando ?? 0) })}
                  </span>}
              <span style={{ flex: 1 }} />
              {e.edit_count > 0 && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ss.edited', { n: String(e.edit_count) })}</span>}
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{e.sent_by ?? '—'}</span>
              <button className="btn ghost sm" onClick={() => abrirEdicion(e)} title={t('ss.edit')}><Pencil size={12} /></button>
              {!e.resolved_at && (
                <select defaultValue="" onChange={(ev) => { if (ev.target.value) resolver(e, ev.target.value); ev.target.value = ''; }} style={{ fontSize: 'var(--fs-2xs)' }}>
                  <option value="">{t('ss.resolve')}</option>
                  {DESENLACES.map((d) => <option key={d} value={d}>{t(`ss.outcome.${d}`)}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
